import { apiClient } from "./api-client";
import type {
  PaymentNetwork,
  BackendBudgetResponse,
  BackendPaymentStats,
  BackendPaymentSettings,
  BackendDepositPayload,
  BackendDepositResponse,
  BackendPayoutUserPayload,
  BackendPayoutModeratorPayload,
  BackendPayoutResponse,
  BackendCreateRequestPayload,
  BackendCreateRequestResponse,
  BackendPayoutRequest,
  BackendAccumulatedAmountResponse,
  BackendAdminPayoutRequestsResponse,
  BackendDepositStatusResponse,
} from "@/lib/types/payments";
import { z } from "zod";

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeRequest = (item: any): BackendPayoutRequest => {
  if (!item) {
    console.warn('[normalizeRequest] Received null or undefined item');
    return {
      id: "",
      userId: 0,
      amount: 0,
      phone: "",
      network: "FLOOZ",
      status: "PENDING",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  const normalized = {
    id: String(item?.id ?? ""),
    userId: toNumber(item?.userId ?? item?.user?.id),
    userName: item?.userName || item?.user?.name || item?.user?.username || undefined,
    userEmail: item?.userEmail || item?.user?.email || undefined,
    amount: toNumber(item?.amount),
    phone: String(item?.phone ?? item?.phone_number ?? ""),
    phone_number: item?.phone_number,
    network: item?.network,
    status: item?.status,
    description: item?.description,
    requestType: item?.requestType || item?.request_type,
    moderatorNote: item?.moderatorNote,
    validatedBy: item?.validatedBy,
    validatedAt: item?.validatedAt,
    paidAt: item?.paidAt,
    txReference: item?.txReference,
    createdAt: item?.createdAt || new Date().toISOString(),
    updatedAt: item?.updatedAt || item?.createdAt || new Date().toISOString(),
  };

  console.log(`[normalizeRequest] Transformed item:`, {
    inputId: item?.id,
    outputId: normalized.id,
    inputAmount: item?.amount,
    outputAmount: normalized.amount,
    inputStatus: item?.status,
    outputStatus: normalized.status,
  });

  return normalized;
};

const paymentSettingsWireSchema = z.object({
  id: z.number().int().positive().optional(),
  amountPerContribution: z.number().int().positive(),
  contributorCap: z.number().int().positive(),
  moderatorAmount: z.number().int().positive(),
  amountPerCommunityContribution: z.number().int().positive(),
  communityCompensationBasis: z.enum(["contribution", "recorded_minute", "hybrid"]),
  amountPerCommunityRecordedMinute: z.number().int().positive(),
  updatedAt: z.string().optional(),
}).strict();

const paymentSettingsResponseSchema = z.union([
  paymentSettingsWireSchema,
  z.object({ success: z.boolean(), settings: paymentSettingsWireSchema }).strict(),
]);

const normalizePaymentSettings = (data: unknown): BackendPaymentSettings => {
  const parsed = paymentSettingsResponseSchema.parse(data);
  const source = "settings" in parsed ? parsed.settings : parsed;
  return {
    contributorAmount: source.amountPerContribution,
    contributorCap: source.contributorCap,
    contributorBudget: 0,
    moderatorAmount: source.moderatorAmount,
    moderatorCount: 0,
    autoPayModerators: false,
    communityCompensationBasis: source.communityCompensationBasis,
    communityAmountPerContribution: source.amountPerCommunityContribution,
    communityAmountPerRecordedMinute: source.amountPerCommunityRecordedMinute,
  };
};

const normalizeAccumulatedAmount = (data: any): BackendAccumulatedAmountResponse => {
  const source = data?.data ?? data ?? {};

  return {
    amount: toNumber(
      source?.amount ?? source?.accumulatedAmount ?? source?.earnedAmount ?? source?.availableAmount ?? source?.total,
    ),
    currency: source?.currency || "XOF",
    lastPayoutDate: source?.lastPayoutDate || source?.last_paid_at || source?.paidAt,
    availableAt: source?.availableAt || source?.available_at,
  };
};

const normalizeAdminRequests = (data: any): BackendAdminPayoutRequestsResponse => {
  const source = data?.data ?? data ?? {};
  const moderatorItems = source?.moderatorRequests?.items || source?.moderatorRequests || source?.moderators || [];
  const contributorItems = source?.contributorRequests?.items || source?.contributorRequests || source?.contributors || [];

  return {
    moderatorRequests: Array.isArray(moderatorItems) ? moderatorItems.map(normalizeRequest) : [],
    contributorRequests: Array.isArray(contributorItems) ? contributorItems.map(normalizeRequest) : [],
    moderatorTotal: toNumber(
      source?.moderatorRequests?.total ?? source?.moderatorTotal ?? source?.moderatorsTotal ?? moderatorItems?.length,
    ),
    contributorTotal: toNumber(
      source?.contributorRequests?.total ?? source?.contributorTotal ?? source?.contributorsTotal ?? contributorItems?.length,
    ),
  };
};

export const payoutsApi = {
  // Budget endpoints
  async getBudget() {
    const { data } = await apiClient.get(`/payments/budget`);
    const total = toNumber(data?.budget?.total ?? data?.budget);
    const reserved = toNumber(data?.budget?.reserved ?? data?.reserved);

    const normalized: BackendBudgetResponse = {
      budget: total,
      reserved,
      available: Math.max(0, total - reserved),
      currency: data?.currency || "XOF",
      lastUpdated: data?.budget?.updatedAt || data?.updatedAt || new Date().toISOString(),
    };

    return normalized;
  },

  async getPaymentStats() {
    const { data } = await apiClient.get(`/payments/stats`);
    const source = data?.stats ?? data ?? {};
    const totalBudget = toNumber(source?.budget?.total ?? source?.budget);
    const reservedBudget = toNumber(source?.budget?.reserved ?? source?.reserved);

    const normalized: BackendPaymentStats = {
      totalPaid: toNumber(source?.totalPaid ?? source?.paidTotal),
      totalPending: toNumber(source?.totalPending ?? source?.pendingRequestsAmount ?? reservedBudget),
      pendingTransactions: toNumber(source?.pendingTransactions ?? source?.pendingRequestsCount),
      pendingRequestsCount: toNumber(source?.pendingRequestsCount ?? source?.pendingTransactions),
      pendingRequestsAmount: toNumber(source?.pendingRequestsAmount ?? source?.totalPending ?? reservedBudget),
      totalRequests: toNumber(source?.totalRequests),
      balance: toNumber(source?.balance ?? source?.available ?? Math.max(0, totalBudget - reservedBudget)),
      successfulTransactions: toNumber(source?.successfulTransactions),
      failedTransactions: toNumber(source?.failedTransactions),
    };

    return normalized;
  },

  async getTransactions(page = 1, limit = 20) {
    const { data } = await apiClient.get(`/payments/transactions`, { params: { page, limit } });
    return data as {
      items: Array<{
        id: number;
        type: "DEPOSIT" | "PAYOUT_USER" | "PAYOUT_MODERATOR";
        amount: number;
        amountPaid?: number;
        status: string;
        txReference?: string;
        phoneNumber?: string;
        network?: string;
        identifier: string;
        createdAt: string;
        user?: { id: number; name?: string; username?: string };
      }>;
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  },

  async getPaymentSettings() {
    const { data } = await apiClient.get(`/payments/settings`);
    return normalizePaymentSettings(data);
  },

  async updatePaymentSettings(payload: BackendPaymentSettings) {
    const { data } = await apiClient.post(`/payments/settings`, {
      amountPerContribution: payload.contributorAmount,
      contributorCap: payload.contributorCap,
      moderatorAmount: payload.moderatorAmount,
      amountPerCommunityContribution: payload.communityAmountPerContribution,
      communityCompensationBasis: payload.communityCompensationBasis,
      amountPerCommunityRecordedMinute: payload.communityAmountPerRecordedMinute,
    });
    return normalizePaymentSettings(data);
  },

  async depositAdmin(payload: BackendDepositPayload) {
    const { data } = await apiClient.post<BackendDepositResponse>(`/payments/deposit`, payload);
    return data;
  },

  /** Polling en attente du webhook FeexPay — voir AdminDepositStatusDialog. */
  async getDepositStatus(identifier: string) {
    const { data } = await apiClient.get<BackendDepositStatusResponse>(
      `/payments/deposit/${identifier}`,
    );
    return data;
  },

  // Payouts
  async payoutUser(payload: BackendPayoutUserPayload) {
    const { data } = await apiClient.post<BackendPayoutResponse>(`/payments/payouts/user`, payload);
    return data;
  },

  async payoutModerator(payload: BackendPayoutModeratorPayload) {
    const { data } = await apiClient.post<BackendPayoutResponse>(`/payments/payouts/moderator`, payload);
    return data;
  },

  async getEligibleModerators() {
    const { data } = await apiClient.get<{ moderators: any[] }>(`/payments/moderators/eligible`);
    return data;
  },

  async payAllModerators() {
    const { data } = await apiClient.post<{ success: number; failed: number; total: number }>(
      `/payments/payouts/moderators/all`
    );
    return data;
  },

  async getModeratorAccumulatedAmount(moderatorId: number | string) {
    const { data } = await apiClient.get(`/payments/moderator/${moderatorId}/accumulated`);
    return normalizeAccumulatedAmount(data);
  },

  async getContributorAccumulatedAmount(userId: number | string) {
    const { data } = await apiClient.get(`/payments/contributor/${userId}/accumulated`);
    return normalizeAccumulatedAmount(data);
  },

  // User payout request (created by user)
  async createPayoutRequest(payload: BackendCreateRequestPayload) {
    console.log('[payoutsApi] createPayoutRequest called with payload:', payload);
    try {
      const { userId: _, ...bodyPayload } = payload;
      const response = await apiClient.post(`/payments/requests`, bodyPayload);
      console.log('[payoutsApi] createPayoutRequest HTTP 201 response:', response.data);
      
      // Backend retourne une réponse MINIMALE: { success, id, amount, maxAmount }
      // On doit construire une réponse COMPLÈTE en combinant payload + réponse
      const completeResponse: BackendCreateRequestResponse = {
        id: String(response.data?.id || ''),
        userId: payload.userId,
        amount: response.data?.amount || payload.amount,
        phone: payload.phone,
        network: payload.network,
        status: 'PENDING', // Nouveau, le backend le donne pas mais on sait que c'est PENDING
        description: payload.description,
        requestType: payload.requestType || 'USER',
        createdAt: new Date().toISOString(),
      };
      
      console.log('[payoutsApi] Constructed complete response:', JSON.stringify(completeResponse, null, 2));
      return completeResponse;
    } catch (error: any) {
      console.error('[payoutsApi] createPayoutRequest FAILED:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        errorData: error.response?.data,
        errorMessage: error.message,
      });
      throw error;
    }
  },

  async getUserPayoutRequests() {
    console.log('[payoutsApi] getUserPayoutRequests called');
    try {
      const { data } = await apiClient.get(`/payments/requests/me`);
      console.log('[payoutsApi] Response from GET /payments/requests/me:', data);
      
      // Gérer différentes structures de réponse du backend
      let items: any[] = [];
      if (Array.isArray(data?.items)) {
        items = data.items;
      } else if (Array.isArray(data?.data)) {
        items = data.data;
      } else if (Array.isArray(data)) {
        items = data;
      }
      
      console.log('[payoutsApi] Extracted items count:', items.length);
      
      // Si 0 items, peut-être que la demande vient d'être créée et n'est pas encore dans la DB
      if (items.length === 0) {
        console.warn('[payoutsApi] No payout requests found');
      }
      
      return { 
        items: items.map(item => {
          console.log('[payoutsApi] Normalizing request item:', item);
          return normalizeRequest(item);
        }),
      };
    } catch (error: any) {
      console.error('[payoutsApi] getUserPayoutRequests FAILED:', {
        status: error.response?.status,
        url: error.config?.url,
        errorData: error.response?.data,
        errorMessage: error.message,
      });
      // Retourner une liste vide au lieu de crasher
      return { items: [] };
    }
  },

  async getPayoutRequests(status?: string) {
    const { data } = await apiClient.get<{ items: any[] }>(`/payments/requests`, {
      params: { status },
    });
    return { items: Array.isArray(data?.items) ? data.items.map(normalizeRequest) : [] };
  },

  async getAdminPayoutRequests(modPage = 1, contPage = 1) {
    const { data } = await apiClient.get(`/payments/admin/requests`, {
      params: { modPage, contPage },
    });
    return normalizeAdminRequests(data);
  },

  async validatePayoutRequest(requestId: number, payload: { action: "APPROVED" | "REJECTED"; note?: string }) {
    const { data } = await apiClient.post(`/payments/requests/${requestId}/validate`, payload);
    return data;
  },

  async markRequestAsPaid(requestId: number) {
    const { data } = await apiClient.post(`/payments/requests/${requestId}/paid`);
    return data;
  },

   async createModeratorPayoutRequest(payload: {
    moderatorId: string;
    phone_number: string;
    network: "FLOOZ" | "TMONEY";
    amount: number;
    description?: string;
  }) {
    console.log('[payoutsApi] createModeratorPayoutRequest called with payload:', payload);
    const amount = toNumber(payload.amount);
    if (amount <= 0) {
      console.error('[payoutsApi] Invalid amount:', amount);
      throw new Error("Montant invalide");
    }

    try {
      // Backend currently exposes only POST /payments/requests for payout requests.
      const transformedPayload = {
        amount,
        phone: payload.phone_number,
        network: payload.network,
      };
      console.log('[payoutsApi] Sending transformed payload:', transformedPayload);
      
      const response = await apiClient.post(`/payments/requests`, transformedPayload);
      console.log('[payoutsApi] createModeratorPayoutRequest HTTP response:', response.data);
      
      // Backend retourne une réponse MINIMALE: { success, id, amount, maxAmount }
      // On doit construire une réponse COMPLÈTE
      const completeResponse = {
        id: String(response.data?.id || ''),
        userId: Number(payload.moderatorId),
        amount: response.data?.amount || amount,
        phone: payload.phone_number,
        network: payload.network,
        status: 'PENDING',
        description: payload.description,
        requestType: "MODERATOR",
        createdAt: new Date().toISOString(),
      };
      
      console.log('[payoutsApi] Constructed complete moderator response:', JSON.stringify(completeResponse, null, 2));
      return completeResponse;
    } catch (error: any) {
      console.error('[payoutsApi] createModeratorPayoutRequest FAILED:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        errorData: error.response?.data,
        errorMessage: error.message,
      });
      throw error;
    }
  },

  /**
   * Récupérer mes demandes en tant que modérateur
   */
  async getModeratorPayoutRequests() {
    try {
      const { data } = await apiClient.get(`/payments/requests/me`);
      return {
        items: Array.isArray(data?.items)
          ? data.items.map(normalizeRequest)
          : Array.isArray(data)
            ? data.map(normalizeRequest)
            : [],
      };
    } catch (error: any) {
      console.error('[payoutsApi] getModeratorPayoutRequests FAILED:', {
        status: error.response?.status,
        url: error.config?.url,
        errorData: error.response?.data,
        errorMessage: error.message,
      });
      return { items: [] };
    }
  },

  /**
   * Récupérer toutes les demandes avec filtres (Admin)
   * Maintenant avec paramètre 'type' en plus de 'status'
   */
  async getPayoutRequestsFiltered(status?: string, type?: "USER" | "MODERATOR") {
    const params: any = {};
    if (status) params.status = status;
    if (type) params.type = type;

    const { data } = await apiClient.get(`/payments/requests`, { params });
    return { items: Array.isArray(data?.items) ? data.items.map(normalizeRequest) : [] };
  },
   //Annuler une demande de paiement  
  async cancelPayoutRequest(requestId: string) {
    const { data } = await apiClient.post(
      `/payments/requests/${requestId}/cancel`
    );
    return data;
  },

  /**
   * Approuve une demande de paiement (admin)
   * POST /payments/admin/requests/:id/approve
   */
  async approvePayoutRequest(requestId: number, note?: string): Promise<any> {
    const { data } = await apiClient.post(`/payments/admin/requests/${requestId}/approve`, {
      note,
    });

    return {
      success: data.success ?? true,
      status: data.status ?? 'APPROVED',
    };
  },

  /**
   * Rejette une demande de paiement (admin)
   * POST /payments/admin/requests/:id/reject
   */
  async rejectPayoutRequest(requestId: number, note?: string): Promise<any> {
    const { data } = await apiClient.post(`/payments/admin/requests/${requestId}/reject`, {
      note,
    });

    return {
      success: data.success ?? true,
      status: data.status ?? 'REJECTED',
    };
  },

};
